import _ from "lodash";
import { useCallback, useMemo } from "react";
import { CardRewardsEarnRates } from "../../types/CardRewardsEarnRates";

const DEFAULT_SPEND = {
  allPurchases: 1700, // General spending outside specific categories
  dining: 250, // Dining out, restaurants, cafes
  gas: 130, // Gasoline for personal vehicles
  grocery: 330, // Groceries, household food
  streaming: 17, // Streaming services (e.g., Netflix, Hulu)
  drugstore: 42, // Over-the-counter medicine, personal care
  takeout_delivery: 83, // Food delivery and takeout services
  office_supplies: 17, // Work-related supplies, home office purchases
  internet_cable_phone: 100, // Combined cost of internet, cable, and phone services
  travel: {
    general: 250, // General travel expenses
    airline: {
      general: 83, // General airline spending
      united: 25, // United Airlines (specific airlines optional)
      delta: 25, // Delta Airlines
      american: 25, // American Airlines
      southwest: 25, // Southwest Airlines
      alaska: 25, // Alaska Airlines
    },
    hotel: {
      general: 67, // Hotel stays
    },
    transit: 50, // Public transit, Uber/Lyft, etc.
  },
} satisfies CardRewardsEarnRates;

export const useMonthlySpendState = (
  overrides: CardRewardsEarnRates | null, 
  setOverrides: (overrides: CardRewardsEarnRates | ((prev: CardRewardsEarnRates) => CardRewardsEarnRates)) => void
) => {
  const spendWithOverrides = useMemo(() => {
    if (!overrides || Object.keys(overrides).length === 0) {
      return DEFAULT_SPEND;
    }
    const defaultSpend = _.cloneDeep(DEFAULT_SPEND);
    Object.entries(overrides).forEach(([key, value]) => {
      _.set(defaultSpend, key, value);
    });

    return defaultSpend;
  }, [overrides])

  const reset = useCallback((spendCategory?: string) => {
    if (spendCategory) {
      setOverrides((prev: CardRewardsEarnRates) => {
        const newOverrides = _.cloneDeep(prev);
        _.unset(newOverrides, spendCategory);
        return newOverrides;
      })
    } else {
      setOverrides({});
    }
  }, [setOverrides]);

  return [spendWithOverrides, setOverrides, reset] as const;
};
